import { Button } from '@gerdau/hefesto/Button';
import { Skeleton } from '@gerdau/hefesto/Skeleton';
import { TextField } from '@gerdau/hefesto/TextField';
import { Title } from '@gerdau/hefesto/Title';
import React, { useEffect, useState } from 'react';
import { default as SkeletonReact } from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { default as CustomerSelectFacelift } from '../../../components/CustomerSelect/Facelift/index';
import { ApplicationState } from '../../../store';
import { loadCustomerContractsV2 } from '../../../store/ducks/contracts-and-constructions/actions';
import { ICustomer } from '../../../store/ducks/customers/types';
import { IFabricationContract } from '../../../store/ducks/fabrication-contracts/types';
import { sendTagging } from '../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../utils/Tagging';
import { uppercaseOnlyFirstLetter } from '../../../utils/formatStrings';
import ContractsAndConstructionsFilters from './ContractsAndConstructionsFilters';
import ContractsAndConstructionsTable from './ContractsAndConstructionsTable';
import {
  Container,
  ContractsAndConstructionsHeader,
  CustomersFilterContainer
} from './styles';

interface IContractsAndConstructionHomeProps {
  customer: ICustomer;
}

const ContractsAndConstructionsHome: React.FC<
  IContractsAndConstructionHomeProps
> = () => {
  const dispatch = useDispatch();
  const contractsManager = useSelector(
    (state: ApplicationState) =>
      state.contractsAndConstructions.contractsManager
  );

  const userState = useSelector((state: ApplicationState) => state.user);

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );

  const customers = useSelector((state: ApplicationState) =>
    state.customers.customers.filter(
      (customer) =>
        userState.roles.ordersInputCustomers.includes(customer.customerId) &&
        userState.roles.fabricationViewCustomers?.includes(customer.customerId)
    )
  );

  const [selectedCustomerId, setSelectedCustomerId] = useState<
    string | undefined
  >(undefined);

  const [filteredContracts, setFilteredContracts] = useState<
    IFabricationContract[]
  >(contractsManager.customerContracts);

  const handleLoadCustomerContracts = () => {
    const needToLoadCustomer =
      userState.isManager &&
      !customersState.customers?.find(
        (c) => c.customerId === selectedCustomerId
      );
    const formatedCustomerId =
      userState.isManager &&
      selectedCustomerId &&
      selectedCustomerId.length < 10
        ? `0${selectedCustomerId}`
        : selectedCustomerId;

    dispatch(
      loadCustomerContractsV2(
        formatedCustomerId ? formatedCustomerId : '',
        needToLoadCustomer
      )
    );
  };

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName: 'LG:ContratosEObras',
        page: window.location.pathname,
        event: 'page_view'
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (contractsManager.customerContracts) {
      setFilteredContracts(contractsManager.customerContracts);
    }
  }, [contractsManager.customerContracts]);

  useEffect(() => {
    if (contractsManager.selectedCustomerId) {
      setSelectedCustomerId(contractsManager.selectedCustomerId);
    }
  }, [contractsManager.selectedCustomerId]);

  const isAllDisabled =
    contractsManager.selectedCustomerId === '' ||
    contractsManager.selectedCustomerId === undefined ||
    contractsManager.loading ||
    contractsManager.error ||
    (contractsManager.customerContracts &&
      contractsManager.customerContracts.length === 0);

  const getSearchButtonText = (): string => {
    if (contractsManager.loading) {
      return 'Buscando';
    } else if (
      contractsManager.selectedCustomerId === selectedCustomerId &&
      contractsManager.error
    ) {
      return 'Tentar novamente';
    } else {
      return 'Buscar';
    }
  };

  const getSelectedCustomer = (customerId: string) => {
    return customersState.customers.find(
      (cust) => cust.customerId === customerId
    );
  };

  const { screenName, itemDescription } = getTaggingInformationScreen(
    'contracts_and_contructions_home_emitter_filter'
  );

  return (
    <>
      <ContractsAndConstructionsHeader>
        <Title level={1}>Contratos e obras</Title>
        <span className="subtitle">
          Selecione um Emissor para acompanhar seus contratos e obras.
        </span>
        <CustomersFilterContainer>
          {userState.isManager && (
            <>
              {(contractsManager.loading || customersState.loading) && (
                <Skeleton type="input" />
              )}
              {!contractsManager.loading && !customersState.loading && (
                <TextField
                  label={'Emissor'}
                  name={'emitter'}
                  placeholder={'Digite o BP'}
                  disabled={contractsManager.loading || customersState.loading}
                  value={selectedCustomerId || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSelectedCustomerId(e.target.value)
                  }
                  supportMessage={
                    contractsManager.bpError &&
                    !contractsManager.loading &&
                    !customersState.loading
                      ? 'BP não encontrado'
                      : (!contractsManager.loading || customersState.loading) &&
                        customersState.customers.length > 0 &&
                        selectedCustomerId &&
                        selectedCustomerId.length > 6
                      ? uppercaseOnlyFirstLetter(
                          getSelectedCustomer(selectedCustomerId)?.name || '',
                          35
                        )
                      : undefined
                  }
                />
              )}
            </>
          )}
          {!userState.isManager && (
            <>
              {customersState.loading && <Skeleton type="input" />}
              {!customersState.loading && (
                <CustomerSelectFacelift
                  title="Emissor"
                  disabled={contractsManager.loading || customersState.loading}
                  placeholder="Selecione um Emissor"
                  customers={customers}
                  onSelect={(customerId) => setSelectedCustomerId(customerId)}
                  selectedCustomerId={selectedCustomerId}
                  loading={contractsManager.loading || customersState.loading}
                ></CustomerSelectFacelift>
              )}
            </>
          )}

          <Button
            fullWidth
            variant="primary"
            onClick={() => {
              handleLoadCustomerContracts();
              dispatch(
                sendTagging({
                  event: 'click',
                  itemDescription: `${itemDescription}_buscar`,
                  itemName: 'button:buscar',
                  screenName,
                  filterInfo: `#codEmr:[${selectedCustomerId}]`
                })
              );
            }}
            loading={contractsManager.loading || customersState.loading}
            disabled={
              contractsManager.loading ||
              (contractsManager.selectedCustomerId === selectedCustomerId &&
                !contractsManager.error)
            }
            name={'search'}
          >
            {getSearchButtonText()}
          </Button>
        </CustomersFilterContainer>
      </ContractsAndConstructionsHeader>

      <Container>
        <ContractsAndConstructionsFilters
          customerContracts={contractsManager.customerContracts}
          setFilteredContracts={setFilteredContracts}
          onCleanFilters={() =>
            setFilteredContracts(contractsManager.customerContracts)
          }
          isAllDisabled={
            contractsManager.selectedCustomerId === '' ||
            contractsManager.selectedCustomerId === undefined ||
            contractsManager.loading ||
            contractsManager.error ||
            (contractsManager.customerContracts &&
              contractsManager.customerContracts.length === 0)
          }
        />
      </Container>

      <Container>
        {contractsManager.selectedCustomerId &&
          !contractsManager.loading &&
          !contractsManager.error && (
            <ContractsAndConstructionsTable
              filteredContracts={filteredContracts}
              isAllDisabled={isAllDisabled}
              contractsManager={contractsManager}
            />
          )}

        {contractsManager.loading && (
          <SkeletonReact count={6} style={{ margin: '0.5rem 0' }} />
        )}
      </Container>
    </>
  );
};

export default ContractsAndConstructionsHome;
